import { useState } from 'react'
import {
  Breadcrumbs, Typography, Stack, Paper, Box, Chip, Button, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, CircularProgress,
} from '@mui/material'
import { CheckCircleOutlined, Cancel } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listMyApprovals, approveRequest, rejectRequest, type ApprovalRequestListItem } from '../../api/aprobari'
import { errorMessage } from '../../api/client'

export default function AprobarileMelePage() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['aprobari', 'mele'], queryFn: listMyApprovals })
  const [decision, setDecision] = useState<{ item: ApprovalRequestListItem; approve: boolean } | null>(null)
  const [comment, setComment] = useState('')

  const mutation = useMutation({
    mutationFn: () => decision!.approve
      ? approveRequest(decision!.item.id, comment || undefined)
      : rejectRequest(decision!.item.id, comment || undefined),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['aprobari'] })
      setDecision(null)
      setComment('')
    },
  })

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Acasă</Typography>
        <Typography variant="inherit" color="text.primary">Aprobările mele</Typography>
      </Breadcrumbs>

      <Typography variant="h2">Aprobările mele</Typography>
      <Typography variant="body2" color="text.secondary">
        Cererile de aprobare pe care le puteți decide — inclusiv cele delegate către dvs.
        pe perioada absenței altui aprobator (FR-ADM-010).
      </Typography>

      <Stack spacing={1}>
        {!isLoading && data?.length === 0 && (
          <Typography variant="body2" color="text.secondary">Nicio cerere în așteptare.</Typography>
        )}
        {data?.map((item) => (
          <Paper key={item.id} variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.documentSummary}</Typography>
                <Chip label={`Pas ${item.currentStepOrder}/${item.totalSteps}`} size="small" variant="outlined" />
                {item.amount != null && (
                  <Chip label={`${item.amount} ${item.currency ?? ''}`} size="small" variant="outlined" />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Cerut de {item.requestedByUserName} · {new Date(item.requestedAt).toLocaleString('ro-RO')}
              </Typography>
            </Box>
            <Button size="small" color="error" startIcon={<Cancel />}
              onClick={() => setDecision({ item, approve: false })}>
              Respinge
            </Button>
            <Button size="small" variant="contained" color="success" startIcon={<CheckCircleOutlined />}
              onClick={() => setDecision({ item, approve: true })}>
              Aprobă
            </Button>
          </Paper>
        ))}
      </Stack>

      <Dialog open={!!decision} onClose={() => setDecision(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{decision?.approve ? 'Aprobare cerere' : 'Respingere cerere'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Typography variant="body2">{decision?.item.documentSummary}</Typography>
            <TextField
              label="Comentariu (opțional)" multiline minRows={2} fullWidth
              value={comment} onChange={(e) => setComment(e.target.value)}
            />
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDecision(null)}>Renunță</Button>
          <Button
            variant="contained" color={decision?.approve ? 'success' : 'error'} disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={() => mutation.mutate()}
          >
            Confirmă
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}
